import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { reportDataMedChangesRe } from '@constants/reports/report-med-changes-re';
import { schedulesHalfHourOptions } from '@constants/schedules';
import { SelectOption } from '@interfaces/input-select-option.interface';
import {
    PatientPharma,
    Report,
} from '@interfaces/report/report.interface';
import { Utils } from '@json/src/app/Utils';
import { CallsService } from '@services/api/calls.service';
import { DateService } from '@services/date.service';
import { DocumentService } from '@services/document.service';
import { InstitutionService } from '@services/institution.service';
import { ReportsService } from '@services/reports/reports.service';
import { RoleManagerService } from '@services/role-manager.service';
import { Title } from '@angular/platform-browser';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import moment from 'moment';
import { LoggerService } from '@services/logger.service';
import { UserAction } from '@enums/user-action';

@Component({
    selector: 'app-report-med-changes-re',
    templateUrl: './report-med-changes-re.component.html',
    styleUrls: ['../report-change-medications/report-change-medications.component.scss'],
})
export class ReportMedChangesReComponent {
    public reportData = reportDataMedChangesRe;
    public showPatientSelect: boolean = false;
    public currentInstitution: number;
    public form: FormGroup;
    public currentRoute: string;
    public report: Report;
    public pathReportDownload: string;
    public safeURL: SafeUrl;
    public selectOptionsPatients: SelectOption[];
    public schedulesOptions = schedulesHalfHourOptions;

    public selectOptionsInsitutions: SelectOption[];
    public typeGlobal: boolean = false;
    public institutionSelected: boolean = false;

    constructor(
        private router: Router,
        private formBuilder: FormBuilder,
        private dateService: DateService,
        private calls: CallsService,
        private reportService: ReportsService,
        private documentService: DocumentService,
        private institutionService: InstitutionService,
        private roleManager: RoleManagerService,
        private titleService: Title,
        private gaService: GoogleAnalyticsService,
        private logger: LoggerService
    ) {
        this.form = this.formBuilder.group({
            startDate: [moment()],
            endDate: [moment().add(1, 'week')],
            selectedPatient: [''],
            // Ocultar (inverted: checked = hide)
            checkboxPayment: [false],
            // Agrupar por
            checkboxDoctorSS: [true],
            selectedOptionFrom: ['00:00'],
            selectedOptionUntil: ['23:00'],
            selectedInstitution: ['', Validators.required],
        });
        this.currentRoute = this.router.url;
        this.currentInstitution = this.institutionService.getCurrentInstitution();
    }

    ngOnInit(): void {
        this.titleService.setTitle(`Reporte: Cambios de medicación + RE`);
        this.gaService.pageView(`${window.location.href}`, `Reporte: Cambios de medicación + RE`);
        this.logger.logPage('Reporte: Cambios de medicación + RE');

        this.typeGlobal = this.roleManager.isPharma() || this.roleManager.isHospitalPharma();

        this.getDataForTheSelects();
        this.setListenerInstitution();
    }

    setListenerInstitution(): void {
        this.form.get('selectedInstitution')?.valueChanges.subscribe((value) => {
            const insId = value?.id;
            if (insId) {
                this.institutionSelected = true;
                this.loadParamsForInstitution(insId);
            } else {
                this.institutionSelected = false;
                this.selectOptionsPatients = [];
            }

            // Reset patient filter on institution change
            this.showPatientSelect = false;
            this.form.patchValue({
                selectedPatient: '',
            });
        });
    }

    loadParamsForInstitution(insId: number): void {
        this.reportService
            .getRepParamsEditDataMedchanges(insId)
            .subscribe((result) => {
                this.selectOptionsPatients = result.payload.pats.map((patient: PatientPharma) => { return { label: patient.nam, value: patient.id } });
            });
    }

    getDataForTheSelects(): void {
        this.institutionService.getInstitutions().then(() => {
            this.selectOptionsInsitutions =
              Object.assign([], this.institutionService.institutionList.getValue());

            this.selectOptionsInsitutions = this.selectOptionsInsitutions.sort((a, b) => a.label.localeCompare(b.label))

            if (this.selectOptionsInsitutions) {
              const hasEmptyValue = this.selectOptionsInsitutions.some(
                (item) => item.value === ''
              );
              if (hasEmptyValue) {
                this.selectOptionsInsitutions = this.selectOptionsInsitutions.filter(
                  (item) => item.value !== ''
                );
              }
            }

            if ( !this.typeGlobal ) {
                this.form.controls['selectedInstitution'].setValue({id: this.institutionService.getCurrentInstitution()})
            }
          });
    }

    handlePreviewClick() {
        if (this.form.valid) {
            this.setInfoRP(true);
            this.reportService.handlePreviewClick(this.router, this.reportData);

            this.logger.logAction(UserAction.Click, {
                message: 'Vista previa',
                ...this.reportData.data
            });
        } else {
            this.showFormError();
        }
    }

    handlePrintClick() {
        if (this.form.valid) {
            this.setInfoRP();
            this.reportService.handlePrintClick(this.reportData);

            this.logger.logAction(UserAction.Click, {
                message: 'Impresión',
                ...this.reportData.dataRenderReport
            });
        } else {
            this.showFormError();
        }
    }

    handlePdfClick() {
        if (this.form.valid) {
            this.setInfoRP();
            this.reportService.handlePdfClick(this.reportData);

            this.logger.logAction(UserAction.Click, {
                message: 'Exportar PDF',
                ...this.reportData.dataDownloadPDF
            });
        } else {
            this.showFormError();
        }
    }

    handleExcelClick() {
        if (this.form.valid) {
            this.setInfoRP();
            this.reportService.handleExcelClick(this.reportData);

            this.logger.logAction(UserAction.Click, {
                message: 'Exportar Excel',
                ...this.reportData.dataDownloadExcel
            });
        } else {
            this.showFormError();
        }
    }

    showFormError(): void {
        if (this.form.value.selectedInstitution == '') {
            this.calls.openSnack('Debes seleccionar un centro');
        } else {
            this.calls.openSnack('Debes seleccionar un intervalo de fechas');
        }
    }

    setInfoRP(isPreview: boolean = false): void {
        const startDate = this.dateService.formatDateToString(this.form.get('startDate')?.value);
        const endDate = this.dateService.formatDateToString(this.form.get('endDate')?.value);
        const selectedPatient = this.form.get('selectedPatient')?.value;
        const checkboxPayment = this.form.get('checkboxPayment')?.value;
        const checkboxDoctorSS = this.form.get('checkboxDoctorSS')?.value;
        const selectedOptionFrom = this.form.get('selectedOptionFrom')?.value;
        const selectedOptionUntil = this.form.get('selectedOptionUntil')?.value;

        const startDateRP = this.dateService.transformToDateWithSpecificHours(startDate, selectedOptionFrom);
        const endDateRP = this.dateService.transformToDateWithSpecificHours(endDate, selectedOptionUntil);

        let currentInstitution;
        const dataForm = this.form.getRawValue();
        //@ts-ignore
        currentInstitution = dataForm.selectedInstitution.id;

        const rp = JSON.stringify({
            LocaleId: '3082',
            InstitutionId: String(currentInstitution),
            PatientId: selectedPatient ? String(selectedPatient) : null,
            DateFrom: startDateRP,
            DateUntil: endDateRP,
            ShowPAGO: checkboxPayment ? 'false' : 'true',
            OnlyOutsidePharma: 'false',
            ShowOutsidePharma: 'false',
            GroupMedicoSS: checkboxDoctorSS ? 'true' : null,
        });

        this.reportData.data.rp = rp;
        if (isPreview) this.reportData.data.dn = Utils.buildUniqueFileName + "_{0}.html";
        this.reportData.dataDownloadPDF.rp = rp;
        this.reportData.dataDownloadExcel.rp = rp;
        this.reportData.dataRenderReport.rp = rp;

        const now = moment();
        this.reportData.dataDownloadPDF.dn = this.reportData.dataRenderReport.dn =
            `Reporte_de_cambios_medicacion_re_${this.institutionService.getCurrentInstitutionShortName()}_${now.format('YYYYMMDD')}_${now.format('HHMM')}.pdf`;
        this.reportData.dataDownloadExcel.dn = `Reporte_de_cambios_medicacion_re_${this.institutionService.getCurrentInstitutionShortName()}_${now.format('YYYYMMDD')}_${now.format('HHMM')}.xls`;
    }

    toggleSelectPatient(): void {
        this.showPatientSelect = !this.showPatientSelect;
        if (!this.showPatientSelect) {
            this.form.patchValue({
                selectedPatient: '',
            });
        }
    }
}
